// CommonJS for CRA and Node. Model facts never come from carrier rules.
const s = require('./madarSchemaUtils');
const CABINETS = ['odc', 'mfat', 'fat', 'bfat', 'atb', 'wall_mount', 'fdc', 'cdb'];
const KINDS = {};
// label_fa is the display label. It holds the OSS-GIS English name the Irancell team works with (2026-10-25).
function kind(name, label_fa, geom, parents = [], spec = true, children = [], parent_required = false) {
  KINDS[name] = { kind: name, label_fa, geom, parents, parent_required, spec_required: spec, generated_children: children };
}
kind('pop', 'POP', 'Point');
kind('room', 'Room', null, ['pop'], false);
kind('rack', 'Rack', null, ['room']);
kind('olt', 'OLT', 'Point', ['pop', 'room', 'odc']);
kind('olt_card', 'OLT Card', null, ['olt'], true, ['pon'], true);
kind('fdp', 'FDP', null, ['pop', 'room'], true, ['ports', 'internal_edges', 'trays']);
CABINETS.forEach((name, i) => kind(name, ['ODC', 'MFAT', 'FAT', 'BFAT', 'ATB', 'Wall Mount', 'FDC', 'CDB'][i],
  'Point', name === 'odc' ? [] : ['pole'], true, ['front_ports', 'internal_edges', 'trays']));
kind('patch_panel', 'Patch Panel', null, ['odc', 'mfat', 'fat', 'fdc', 'pop', 'room'], true, ['ports', 'internal_edges', 'trays']);
kind('splitter', 'Splitter', null, ['odc', 'mfat', 'fat', 'bfat', 'fdc', 'cdb', 'atb', 'splice_closure'], true, ['splitter_in', 'splitter_out']);
kind('splice_closure', 'Splice Closure', 'Point', ['handhole', 'manhole', 'pole'], true, ['trays'], true);
kind('handhole', 'Handhole', 'Point');
kind('manhole', 'Manhole', 'Point');
kind('t_connector', 'T Connector', 'Point', ['handhole', 'manhole']);
kind('h_connector', 'H Connector', 'Point', ['handhole', 'manhole']);
kind('coupler', 'Coupler', 'Point', ['handhole', 'manhole']);
kind('pole', 'Pole', 'Point');
kind('structure', 'Structure', 'Point', [], false);
kind('building', 'Building', 'Point', [], false);
kind('trench', 'Trench', 'LineString');
kind('microduct', 'Microduct', 'LineString', [], true, ['channels', 'channel_slots']);
kind('pe_pipe', 'PE Pipe', 'LineString');
kind('gi_pipe', 'GI Pipe', 'LineString');
kind('span', 'Span', 'LineString', [], false);
kind('cable', 'Cable', 'LineString', [], true, ['fibre_end_A', 'fibre_end_B']);
kind('coverage_area', 'Coverage Area', 'Polygon', [], false);
kind('survey_area', 'SurveyArea', 'Polygon', [], false);
kind('dsa_boundary', 'DSA_Boundary', 'Polygon', [], false);
kind('csa_boundary', 'CSA_Boundary', 'Polygon', [], false);
const KIND_NAMES = Object.keys(KINDS);
const STATUSES = ['P', 'I', 'O'];
const LAYERS = ['metro', 'odc', 'fat', 'drop', 'backbone', 'other'];
const LAYER_LABELS = { metro: 'Metro', odc: 'ODC', fat: 'FAT', drop: 'Drop', backbone: 'Backbone', other: 'Other' };
const PROVENANCE = ['fact', 'generated'];
const MEDIA = ['splice', 'connector'];
const medium = s.enumeration(...MEDIA);
const mediaSides = s.shape({ in: medium, out: medium }, ['in']);
const colors = s.array(s.shape({ name_fa: s.text, hex: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$' } }));
const MODEL_FACTS = { adapter_passthrough: s.nullable({ type: 'boolean' }), recorded_internal_edges: s.enumeration(true, null) };
const commonDevice = {
  splice_capacity: s.nullable(s.integer(0)), tray_template: s.nullable(s.array(s.shape({ name: s.text, slots: s.integer(1) }))),
  allowed_child_spec_ids: s.nullable(s.array(s.uuid, { uniqueItems: true })), recorded_internal_edges: MODEL_FACTS.recorded_internal_edges,
};
const cabinet = Object.assign({}, commonDevice, {
  front_ports: s.nullable(s.integer(0)), front_sides: s.enumeration(1, 2),
  front_media: medium, side_media: mediaSides, adapter_passthrough: MODEL_FACTS.adapter_passthrough,
});
const panel = Object.assign({}, commonDevice, {
  port_count: s.integer(1, 576), sides_per_port: s.enumeration(1, 2),
  side_media: mediaSides, adapter_passthrough: MODEL_FACTS.adapter_passthrough, connector: s.text,
});
const definitions = {
  cable: { core_count: s.integer(2, 288), tube_count: s.integer(1), fibres_per_tube: s.integer(1),
    category: s.enumeration('underground', 'aerial_adss', 'indoor', 'drop'), tube_colors: colors, fibre_colors: colors },
  microduct: { channel_count: s.integer(1, 24), channel_colors: colors, size_label: s.text, channel_class: s.enumeration('7_way', '4_way') },
  trench: { trench_type: s.text, method: s.enumeration('hand', 'machine') },
  fdp: panel, patch_panel: panel, splice_closure: commonDevice,
  splitter: { inputs: s.enumeration(1, 2), outputs: s.integer(2, 64), legs: s.enumeration('pigtail', 'connector') },
  olt: { u_height: s.integer(1), card_slots: s.integer(1, 32) },
  olt_card: { pon_ports: s.integer(1, 64), technology: s.enumeration('gpon', 'xgspon') }, rack: { u_height: s.integer(1, 60) },
  handhole: { dimensions: s.text, body_material: s.text }, manhole: { dimensions: s.text, body_material: s.text },
  pole: { height_m: s.number(0), pole_material: s.text, pole_type: s.text },
  pe_pipe: { diameter_mm: s.number(0) }, gi_pipe: { diameter_mm: s.number(0) },
  t_connector: { internal_dims: s.text, external_dims: s.text }, h_connector: { internal_dims: s.text, external_dims: s.text },
  coupler: { internal_dims: s.text, external_dims: s.text },
};
CABINETS.forEach((name) => { definitions[name] = cabinet; });
const required = {
  cable: ['core_count', 'tube_count', 'fibres_per_tube'], microduct: ['channel_count', 'channel_class'],
  fdp: ['port_count', 'sides_per_port', 'side_media', 'adapter_passthrough'],
  patch_panel: ['port_count', 'sides_per_port', 'side_media', 'adapter_passthrough'],
  splitter: ['inputs', 'outputs', 'legs'], olt: ['card_slots'], olt_card: ['pon_ports'], rack: ['u_height'],
};
CABINETS.forEach((name) => { required[name] = ['adapter_passthrough']; });
const SPEC_ATTRS = Object.fromEntries(KIND_NAMES.map((name) => [name, s.shape(Object.assign({ material: s.text }, definitions[name] || {}), required[name] || [])]));
const LOCKED_SPEC_KEYS = Object.fromEntries(KIND_NAMES.map((name) => [name, []]));
LOCKED_SPEC_KEYS.cable = ['core_count', 'tube_count', 'fibres_per_tube', 'tube_colors', 'fibre_colors'];
LOCKED_SPEC_KEYS.microduct = ['channel_count', 'channel_colors', 'channel_class'];
['splice_closure', 'fdp', 'patch_panel', ...CABINETS].forEach((name) => { LOCKED_SPEC_KEYS[name] = Object.keys(definitions[name]).filter((key) => key !== 'connector'); });
LOCKED_SPEC_KEYS.splitter = ['inputs', 'outputs', 'legs'];
LOCKED_SPEC_KEYS.olt = ['u_height', 'card_slots'];
LOCKED_SPEC_KEYS.olt_card = ['pon_ports'];
LOCKED_SPEC_KEYS.rack = ['u_height'];
const INSTANCE_ATTRS = Object.fromEntries(KIND_NAMES.map((name) => [name, s.shape(Object.assign(
  { remarks: { type: 'string' } },
  name === 'pole' ? { pole_number: s.text, address: s.text } : {},
  name === 'olt' ? { ip_address: s.text } : {},
  name === 'rack' ? { room_x: { type: 'number' }, room_y: { type: 'number' }, width: s.number(0), depth: s.number(0) } : {},
  name === 'splitter' ? { level: s.enumeration('primary', 'secondary') } : {},
  KINDS[name].geom === 'LineString' && name !== 'cable'
    ? { construction: s.enumeration('surveyed', 'stub', 'bridge', 'fallback') } : {},
), [])]));
function validateSpecAttrs(name, attrs, { settings = {} } = {}) {
  if (!s.own(SPEC_ATTRS, name)) return s.result(null, [{ code: 'INVALID_VALUE', field: 'kind' }]);
  const errors = s.check(SPEC_ATTRS[name], attrs);
  const fail = (field, reason) => errors.push({ code: 'INVALID_VALUE', field, reason });
  if (s.object(attrs)) {
    if (name === 'cable') {
      if (!(settings.cable_core_counts || [12, 24, 48, 72, 96]).includes(attrs.core_count)) fail('core_count', 'not_allowed');
      if (attrs.tube_count * attrs.fibres_per_tube !== attrs.core_count) fail('core_count', 'tube_product');
    }
    [['tube_colors', 'tube_count'], ['fibre_colors', 'fibres_per_tube'], ['channel_colors', 'channel_count']].forEach(([key, count]) => {
      if (Array.isArray(attrs[key]) && attrs[key].length !== attrs[count]) fail(key, 'length');
    });
    if (CABINETS.includes(name) && attrs.front_ports != null) {
      if (![1, 2].includes(attrs.front_sides)) fail('front_sides', 'required');
      if (attrs.front_sides === 1 && !MEDIA.includes(attrs.front_media)) fail('front_media', 'required');
      if (attrs.front_sides === 2 && !s.object(attrs.side_media)) fail('side_media', 'required');
    }
    const sides = CABINETS.includes(name) ? attrs.front_sides : attrs.sides_per_port;
    if (sides === 2 && (!s.object(attrs.side_media) || !MEDIA.includes(attrs.side_media.out))) fail('side_media.out', 'required');
    if (attrs.adapter_passthrough === true && (sides !== 2 || (CABINETS.includes(name) && attrs.front_ports == null))) fail('adapter_passthrough', 'two_sides_required');
    if (Array.isArray(attrs.tray_template) && Number.isInteger(attrs.splice_capacity)
      && attrs.tray_template.reduce((sum, tray) => sum + (tray?.slots || 0), 0) > attrs.splice_capacity) fail('tray_template', 'capacity');
  }
  return s.result(attrs, errors);
}
function validateInstanceAttrs(name, attrs) {
  return s.result(attrs, s.own(INSTANCE_ATTRS, name) ? s.check(INSTANCE_ATTRS[name], attrs) : [{ code: 'INVALID_VALUE', field: 'kind' }]);
}
function normalizeFilter(input = {}) {
  const schema = s.shape({ kinds: s.array(s.enumeration(...KIND_NAMES)), layers: s.array(s.enumeration(...LAYERS)),
    statuses: s.array(s.enumeration(...STATUSES)), provenance: s.array(s.enumeration(...PROVENANCE)), region_id: s.nullable(s.uuid) }, []);
  if (s.check(schema, input).length) throw new TypeError('Invalid Madar filter');
  const out = {};
  ['kinds', 'layers', 'statuses', 'provenance'].forEach((key) => { out[key] = [...new Set(input[key] || [])].sort(); });
  out.region_id = input.region_id ? input.region_id.toLowerCase() : null;
  return out;
}
// Template body: single = one item, group = ordered items, explicit parent_key only.
function validateTemplate(input, { specsById, carrierId } = {}) {
  const itemSchema = s.shape({ key: s.text, kind: s.enumeration(...KIND_NAMES), spec_id: s.nullable(s.uuid),
    parent_key: s.nullable(s.text), attrs: { type: 'object' } }, ['key', 'kind']);
  const errors = s.check(s.shape({ name: s.text, mode: s.enumeration('single', 'group'), body: s.shape({ items: s.array(itemSchema, { minItems: 1 }) }) }, ['mode', 'body']), input);
  const seen = new Map();
  if (s.object(input) && Array.isArray(input.body?.items)) {
    if (input.mode === 'single' && input.body.items.length !== 1) errors.push({ code: 'INVALID_VALUE', field: 'body.items', reason: 'single' });
    input.body.items.forEach((item, i) => {
      const field = `body.items[${i}]`;
      if (!s.object(item) || !s.own(KINDS, item.kind)) return;
      if (seen.has(item.key)) errors.push({ code: 'DUPLICATE', field: `${field}.key` });
      if (KINDS[item.kind].spec_required && !item.spec_id) errors.push({ code: 'REQUIRED_FIELD', field: `${field}.spec_id` });
      if (item.parent_key && (!seen.has(item.parent_key) || !KINDS[item.kind].parents.includes(seen.get(item.parent_key)))) errors.push({ code: 'PARENT_NOT_FOUND', field: `${field}.parent_key` });
      const spec = specsById instanceof Map ? specsById.get(item.spec_id) : specsById?.[item.spec_id];
      if (specsById && item.spec_id && (!spec || spec.kind !== item.kind || spec.status !== 'active' || (carrierId && spec.carrier_id !== carrierId))) errors.push({ code: 'SPEC_MISMATCH', field: `${field}.spec_id` });
      validateInstanceAttrs(item.kind, item.attrs || {}).errors.forEach((error) => errors.push(Object.assign({}, error, { field: `${field}.attrs.${error.field}` })));
      seen.set(item.key, item.kind);
    });
  }
  return s.result(input, errors);
}
const ENDPOINT_MEDIA = { fibre_end: 'splice', pon: 'connector', port_in: 'side_media.in', port_out: 'side_media.out',
  port: { cabinet: 'front_media', panel: 'side_media.in' }, splitter_in: 'legs', splitter_out: 'legs' };
const SPLITTER_MEDIA = { pigtail: 'splice', connector: 'connector' };
function endpointMedium(endpointKind, attrs = {}, { cabinet: isCabinet = false } = {}) {
  if (endpointKind === 'fibre_end') return 'splice';
  if (endpointKind === 'pon') return 'connector';
  if (['splitter_in', 'splitter_out'].includes(endpointKind)) return SPLITTER_MEDIA[attrs.legs] || null;
  const value = endpointKind === 'port_in' ? attrs.side_media?.in : endpointKind === 'port_out' ? attrs.side_media?.out
    : endpointKind === 'port' ? (isCabinet ? attrs.front_media : attrs.side_media?.in) : null;
  return MEDIA.includes(value) ? value : null;
}
const MAP_STYLES = { statuses: { P: { color: '#475569', dash: [6, 4] }, I: { color: '#a16207', dash: [2, 3] }, O: { color: '#166534', dash: [] } },
  kinds: Object.fromEntries(KIND_NAMES.map((name) => [name, { geometry: KINDS[name].geom, clickable: false, strokeWeight: 2, fillOpacity: 0.15 }])) };
const IMPORT_FIELDS = { create_asset: ['kind', 'code', 'external_ref', 'name', 'spec', 'vendor', 'material', 'status', 'layer', 'region',
  'project', 'parent', 'end_a', 'end_b', 'container', 'container_interval', 'geometry', 'wkt', 'crs', 'attrs', 'declared_length_m'],
  update_asset: ['target', 'patch'], update_geometry: ['target', 'geometry', 'crs'],
  command: ['op', 'from', 'to', 'spec', 'length_m', 'via', 'channel_number', 'channel_color'] };
IMPORT_FIELDS.by_kind = Object.fromEntries(KIND_NAMES.map((name) => [name, [...IMPORT_FIELDS.create_asset,
  ...Object.keys(INSTANCE_ATTRS[name].properties), ...(name === 'olt_card' ? ['chassis_slot'] : []),
  ...(name === 'cable' ? ['calculated_length_m'] : [])]]));
module.exports = s.freeze({ KINDS, KIND_NAMES, CABINETS, STATUSES, LAYERS, LAYER_LABELS, PROVENANCE, MEDIA, ENDPOINT_MEDIA, SPLITTER_MEDIA, endpointMedium, MODEL_FACTS,
  SPEC_ATTRS, INSTANCE_ATTRS, LOCKED_SPEC_KEYS, MAP_STYLES, IMPORT_FIELDS, normalizeFilter, validateSpecAttrs, validateInstanceAttrs, validateTemplate });
